import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "components";
import { formatUnixTimestamp } from "pages/Opportunities/utils/featureRule";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import ConfirmBookingModal from "./ConfirmBookingModal";
import Overview from "./tabs/Overview";
import { generic_order_status_options } from "common/filters";
import Customers from "./tabs/Customers";
import { Delivery } from "./tabs/Delivery";
import Payments from "./tabs/Payments";
import { useOrder } from "./hooks/useOrders";
import { Milestone, Customers as TypeCustomers } from "./types/order";
import OtherDetails from "./tabs/OtherDetails";
import { getButtonState, getLiveStage } from "./utils/stageconfig";
import { Button } from "@/components/ui/button";
import MilestoneTracker from "./components/milestonetracker";
import { triggerOrderRefresh } from "redux/features/CreateGICOrder";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import BulkUpload from "./components/bulkupload";
import { handleDownloadViaURL } from "utils";
import ProductAndCharges from "./tabs/ProductCharges";
import { FileArchive } from "lucide-react";
import { getAllFiles } from "./apis";
import { toast } from "sonner";

const orderStatusActions = [
  {
    status: "Docs Pending",
    actions: ["Review Documents"],
    tabName: "customers",
  },
  {
    status: "Docs Under Review",
    actions: ["Review Documents"],
    tabName: "customers",
  },
  {
    status: "Docs Verified",
    actions: ["Review Documents"],
    tabName: "customers",
  },
  {
    status: "Docs Rejected",
    actions: ["Review Documents"],
    tabName: "customers",
  },

  {
    status: "Payment Pending",
    actions: ["Complete Payment"],
    tabName: "payments",
  },
  {
    status: "Payment Under Review",
    actions: ["Approve Payment"],
    tabName: "payments",
  },
  { status: "Paid", actions: ["View Receipt"], tabName: "payments" },
  { status: "Payment Rejected", actions: [], tabName: "payments" },

  {
    status: "Delivery",
    actions: ["Mark out for Delivery"],
    tabName: "delivery",
  },

  // { status: "Ready To Deliver", actions: ["Mark out for Delivery", "Initiate doc sign"], tabName: "delivery" },
  // { status: "Out for Delivery", actions: ["Initiate doc sign"], tabName: "delivery" },
  // { status: "Order Delivered", actions: ["Initiate doc sign"], tabName: "delivery" },

  // { status: "Pending", actions: ["Resend Sign LRS"], tabName: "other-details" },
  { status: "Closed", actions: [], tabName: "overview" },
];
// Create an array of objects [{ product_id, status }]


const GenericOrderDetailsPage: React.FC = () => {
  const [isConfirmBooking, setIsConfirmBooking] = useState<boolean>(false);
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const param = useParams();
  const [activeTab, setTab] = useState<string>("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [note, setNote] = useState<string>("");
  const [tempNote, setTempNote] = useState<string>(note);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null
  );
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number, total: number } | null>(null);
  const { orderDetails, isLoading, refetch } = useOrder(id);
  const milestone = orderDetails?.milestones || [];
  const { disabled } = getButtonState("confirmOrder", milestone as Milestone[]);
  const deliveryButtonState = getButtonState(
    "confirmDelivery",
    milestone as Milestone[]
  );
  const isOrderCancelled = milestone?.some(
    (m) =>
      m.stage?.toLowerCase() === "order cancelled" && m.status?.toLowerCase() === "fulfilled"
  );
  const productDeliveryArray = orderDetails?.delivery?.map((delivery) => ({
    product_id: delivery?.generic_order_product_id,
    status: delivery?.Status
  }));

  // Check if all non-cancelled products are delivered
  const areAllNonCancelledProductsDelivered = (() => {
    // Get all products that are not cancelled/rejected
    const activeProducts = orderDetails?.customers?.product_customer?.filter(
      (product) => product.status !== 'Reject'
    ) || [];

    // If no active products, return false
    if (activeProducts.length === 0) return false;

    // Check if all active products have delivery records with "Delivered" status
    const allDelivered = activeProducts.every((product) => {
      const delivery = orderDetails?.delivery?.find(
        (d) => d.generic_order_product_id === product.id
      );

      // Check if delivery exists and status is "Delivered" or "Order Delivered"
      return delivery &&
             (delivery.Status?.toLowerCase() === 'delivered' ||
              delivery.Status?.toLowerCase() === 'order delivered');
    });

    return allDelivered;
  })();

  // Check if there are any active products that need delivery
  const hasActiveProductsForDelivery = (() => {
    // Get all products that are not cancelled/rejected
    const activeProducts = orderDetails?.customers?.product_customer?.filter(
      (product) => product.status !== 'Reject'
    ) || [];

    // Return true if there are active products
    return activeProducts.length > 0;
  })();

  const paymentButtonState = getButtonState("submitPayment", milestone);
  const isDisabledAll = getButtonState("disableAll", milestone);
  const liveStage = getLiveStage(milestone as Milestone[]);
  const shouldRefresh = useAppSelector((state) => state.CreateGICOrder);
  const navigate = useNavigate();
  useEffect(() => {
    if (shouldRefresh) {
      refetch();
      dispatch(triggerOrderRefresh(false));
    }
  }, [shouldRefresh, refetch, dispatch]);

  // Use our custom hook

  const openDetailNote = () => {
    setIsEditing(true);
  };

  const handleConfirmSubmit = () => {
    // Submit logic here
    setIsConfirmBooking(false);
  };

  const handleBulkDownload = async () => {
    if (!orderDetails?.metadata?.order_id) {
      toast.error("Order ID not found");
      return;
    }

    setBulkDownloadLoading(true);
    setDownloadProgress(null);

    try {
      const files = await getAllFiles({ order_id: orderDetails.metadata.order_id });

      if (!files || files.length === 0) {
        toast.error("No  files found for download");
        return;
      }

      const validFiles = files.filter(file => file.document?.url);

      if (validFiles.length === 0) {
        toast.error("No downloadable files found");
        return;
      }

      if (validFiles.length < files.length) {
        toast.warning(`${files.length - validFiles.length} files have no download URL and will be skipped`);
      }

      toast.success(`Combining ${validFiles.length} files into one PDF...`);

      // Dynamically import pdf-lib
      const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");

      const mergedPdf = await PDFDocument.create();

      for (let i = 0; i < validFiles.length; i++) {
        setDownloadProgress({ current: i + 1, total: validFiles.length });
        const url = validFiles[i].document.url;
        const response = await fetch(url);
        const contentType = response.headers.get("Content-Type") || "";
        const buffer = await response.arrayBuffer();

        if (contentType.includes("pdf")) {
          // Merge PDF pages
          try {
            const pdf = await PDFDocument.load(buffer);
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => mergedPdf.addPage(page));
          } catch (err) {
            console.warn("Failed to merge a PDF file:", err);
          }
        } else if (contentType.includes("image")) {
          // Add image as a PDF page
          try {
            const imageBytes = new Uint8Array(buffer);
            let image;
            if (contentType.includes("png")) {
              image = await mergedPdf.embedPng(imageBytes);
            } else {
              image = await mergedPdf.embedJpg(imageBytes);
            }

            const page = mergedPdf.addPage([image.width, image.height]);
            page.drawImage(image, {
              x: 0,
              y: 0,
              width: image.width,
              height: image.height,
            });
          } catch (err) {
            console.warn("Failed to embed image:", err);
          }
        } else {
          console.warn("Skipped unsupported file:", url);
        }
      }

      const mergedPdfBytes = await mergedPdf.save();

      const filename = `OD-${orderDetails.metadata.order_id}_${orderDetails.metadata.created_for?.replace(/[<>:"/\\|?*]/g, '_')}_Documents.pdf`;
      const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`PDF download completed! ${validFiles.length} files merged.`, { duration: 6000 });

    } catch (error) {
      console.error("Error during PDF creation:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to create PDF: ${errorMessage}`);
    } finally {
      setBulkDownloadLoading(false);
      setTimeout(() => setDownloadProgress(null), 2000);
    }
  };


  const onTabChange = (value: string) => {
    setTab(value);
  };

  const colors = generic_order_status_options.find(
    (status) =>
      status.label.toLocaleLowerCase() === liveStage?.stage?.toLowerCase()
  );

  let tabsdata = [
    {
      id: 1,
      label: "Overview",
      value: "overview",
      enabled: true,
      visible: true,
      content: "Make changes to your account here.",
    },
    {
      id: 2,
      label: "Product & Charges",
      value: "product-charges",
      enabled: true,
      visible: true,
      content: "Change your password here.",
    },
    {
      id: 3,
      label: "Travellers",
      value: "travellers",
      enabled: true,
      visible: true,
      content: "Change your password here.",
    },
    {
      id: 7,
      label: "Travellers",
      value: "t",
      enabled: true,
      visible: false,
      content: "Change your password here.",
    },
    {
      id: 4,
      label: "Delivery",
      value: "delivery",
      enabled: true,
      visible: true,
      content: "Change your password here.",
    },
    {
      id: 5,
      label: "Payments",
      value: "payments",
      visible: true,
      enabled: true,
      content: "Change your password here.",
    },
    {
      id: 6,
      label: "Other Details",
      value: "other-details",
      visible: true,
      enabled: true,
      content: "Change your password here.",
    },
  ];

  const transformNewToOldSchema = (newData: TypeCustomers) => {
    // Create a map to group products by customer
    const customersMap = new Map<number, any>();

    newData?.product_customer?.forEach((product) => {
      const customerId = product.created_for;

      // If customer doesn't exist in map, create entry
      if (!customersMap.has(customerId)) {
        customersMap.set(customerId, {
          id: customerId,
          name: product.customer_detail?.full_name || `Customer ${customerId}`,
          product_detail: [],
        });
      }

      // Add product to customer's product_detail array
      const customer = customersMap.get(customerId);
      customer.product_detail.push({
        id: product.id,
        name: product.name,
        currency: product.country_currency_want,
        quantity: product.quantity,
        status: product.status,
        reason_for_reject: product.reason_for_reject || "",
        // Include any other fields your component expects
        subCategory: product.subCategory,
      });
    });

    // Convert map values to array
    return Array.from(customersMap.values());
  };

  const oldSchemaData = transformNewToOldSchema(
    orderDetails?.customers as TypeCustomers
  );
  const customerCount = new Set<number>(
    orderDetails?.customers?.product_customer
      ?.filter((product) => product.status === "Accept" || !product?.status)
      .map((product) => product.created_for)
  );

  const customersForBulkUpload =
    orderDetails?.customers?.product_customer?.map((product) => ({
      id: product.created_for?.toString(),
      name:
        product.customer_detail?.full_name || `Customer ${product.created_for}`,
      status: product.status || "pending",
    })) || [];

  function getActionsBySubStage(subStage?: string): string[] {
    if (!subStage) return [];
    const match = orderStatusActions.find(
      (item) => item.status.toLowerCase() === subStage.toLowerCase()
    );

    return match ? match.actions : [];
  }

  function getStatusDetailsBySubStage(subStage?: string) {
    if (!subStage) return null;
    return orderStatusActions.find(
      (item) => item.status.toLowerCase() === subStage.toLowerCase()
    );
  }

  const actions = getActionsBySubStage(liveStage?.sub_stage);
  const handleQuickActionbtnRedirection = () => {
    const statusDetails = getStatusDetailsBySubStage(liveStage?.sub_stage);

    if (!statusDetails) return;

    const tabToActivate = tabsdata?.find(
      (tab) => tab?.value === statusDetails.tabName
    );

    if (tabToActivate && tabToActivate?.enabled && tabToActivate?.visible) {
      setTab(tabToActivate?.value);
    } else {
      console.warn(
        "No visible/enabled tab matched for sub stage:",
        liveStage?.sub_stage
      );
    }
  };

  const customer_id =
    orderDetails?.created_by_staff && orderDetails?.created_by_staff !== 0
      ? orderDetails?.customers?.product_customer?.[0]?.created_for
      : orderDetails?.created_by_customer;


  const customerKycStatus = orderDetails?.customers?.product_customer?.[0]?.customer_detail?.kyc_status || '';
  const allDocumentsStatus = orderDetails?.order_documents && orderDetails?.order_documents?.length > 0 && orderDetails?.order_documents?.every(doc => doc.status === 'verified');

  const isOrderFullyPaid = (orderDetails?.overview?.amount_and_charges?.balance || 0) <= 0;
  const isOrderDelivered = liveStage?.sub_stage === 'Order Closed';
  const orderType = orderDetails?.metadata?.type_of_order?.toLowerCase() === 'encashment' ? 'sell' : orderDetails?.metadata?.type_of_order;
  const isInvoiceAvalaible = orderDetails?.invoice_document?.document?.url

  return (
    <div className="flex h-full overflow-hidden md:overflow-auto gap-4 md:flex-col">
      {isLoading && <Spinner />}
      <div className="flex flex-col gap-5 min-h-full w-full overflow-y-scroll overflow-x-hidden p-3 bg-white-A700 border rounded-xl">
        <div className="flex w-full gap-8 border-b-2 justify-between pb-5">
          <div className="flex md:flex-col sm:flex-col justify-between items-start lg:items-center w-full">
            <div className=" flex flex-col gap-2">
              <div className="flex flex-row  items-center gap-2 capitalize">
                <h3 className="font-semibold whitespace-nowrap leading-4 text-lg text-black-label">
                  OD-{orderDetails?.metadata.order_id}
                </h3>
                <span className="text-gray-400">•</span>
                <h3 className="font-semibold leading-4 text-lg text-black-label">
                  {orderDetails?.metadata?.created_for}
                </h3>
                <span className="text-gray-400">•</span>
                <span className="font-semibold leading-4 text-lg text-black-label">
                  {orderDetails?.metadata?.product_group}
                </span>
                <span className="text-gray-400">•</span>
                <span className="font-semibold leading-4 capitalize text-lg text-black-label">
                  {orderType}
                </span>
              </div>

              <div>
                <p className="font-normal leading-[22.4px] text-sm text-[#7E7E7E]">
                  <span className="capitalize">created on</span>{" "}
                  <span className="normal-case">{formatUnixTimestamp(
                    orderDetails?.metadata?.created_at as number
                  )}</span>{" "}
                  <span className="capitalize">by</span> {orderDetails?.metadata?.created_by || "N/A"} |{" "}
                  {orderDetails?.metadata?.source || "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                {orderDetails?.invoice_document?.document?.url && (
                  <Button
                    onClick={() =>
                      handleDownloadViaURL(
                        orderDetails?.invoice_document?.document?.url,
                        orderDetails?.invoice_document?.doc_name
                      )
                    }
                    variant="ghost"
                    className="rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-blue-700 text-blue-700 font-semibold text-sm bg-white"
                  >
                    Download invoice
                    <img
                      alt="upload icon"
                      src="/images/remit/uploadIcon.svg"
                      style={{ height: "20px", width: "20px" }}
                      className="rotate-180"
                    />
                  </Button>
                )}
                {orderDetails?.invoice_document?.document?.url && (
                  <Button
                    onClick={handleBulkDownload}
                    variant="ghost"
                    className="rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-blue-700 text-blue-700 font-semibold text-sm bg-white"
                    disabled={bulkDownloadLoading}
                  >
                    {bulkDownloadLoading && downloadProgress ?
                      `Creating Bundle... ${downloadProgress.current}/${downloadProgress.total}` :
                      bulkDownloadLoading ?
                        'Preparing Bundle...' :
                        'Download Bundle'
                    }
                    <FileArchive className="w-4 h-4 text-blue-700" />
                  </Button>
                )}
                {(disabled && !orderDetails?.invoice_document?.document?.url) && (
                  <BulkUpload
                    doc_uuid={orderDetails?.invoice_document?.id || ""}
                    customers={customersForBulkUpload}
                  >
                    <Button
                      variant="ghost"
                      className="rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-blue-700 text-blue-700 font-semibold text-sm bg-white"
                    >
                      Upload invoice
                      <img
                        alt="upload icon"
                        src="/images/remit/uploadIcon.svg"
                        style={{ height: "20px", width: "20px" }}
                      />
                    </Button>
                  </BulkUpload>
                )}
              </div>
              {!disabled && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsConfirmBooking(true)}
                    disabled={disabled}
                    variant="ghost"
                    className={`rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-[#165FE3] text-[#165FE3] font-semibold text-sm bg-white ${isOrderCancelled ? "hidden" : ""
                      }`}
                  >
                    Accept
                  </Button>
                  <Button
                    onClick={() => setIsConfirmBooking(true)}
                    disabled={disabled}
                    variant="ghost"
                    className={`rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-[#EB5757] text-[#EB5757] font-semibold text-sm bg-white ${isOrderCancelled ? "hidden" : ""
                      }`}
                  >
                    Reject
                  </Button>
                </div>
              )}
              {/* {disabled && actions?.[0] && liveStage?.sub_stage !== "Order Created" && (
                <Button
                  onClick={() => handleQuickActionbtnRedirection()}
                  variant="ghost"
                  className="rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-[#165FE3] text-[#165FE3] font-semibold text-sm bg-white"
                >
                  {actions?.[0]}
                </Button>
              )}
              <span
                style={{
                  color: colors?.textColor,
                  backgroundColor: colors?.bgColor,
                }}
                className="rounded-full capitalize py-0.5 px-2  font-semibold text-sm "
              >
                {liveStage?.sub_stage}
              </span> */}
            </div>
          </div>
        </div>

        {/* all tabs */}
        <div className="flex flex-col justify-between h-full ">
          <Tabs
            value={activeTab}
            onValueChange={onTabChange}
            className=" sm:max-w-7xl sm:overflow-hidden"
          >
            <TabsList className="border-b border-[#C4D7F9] w-full overflow-x-auto hide-scrollbar flex justify-between rounded-none gap-2 h-12">
              {tabsdata?.map((tab) => {
                if (!tab?.visible) return null;
                if (tab.value === "travellers") {
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.value}
                      disabled={!tab.enabled}
                      className={`px-6 py-3.5 h-11 border-transparent border-t  w-[215px] text-sm ${tab.value === activeTab &&
                        " rounded-none border-blue-700 text-blue-700 shadow bg-[#EFF4FF]"
                        }`}
                    >
                      <div className="flex gap-2 items-center">
                        {tab.label}{" "}
                        <span className=" text-center flex items-center gap-1.5">
                          {
                            // Show warning if KYC is not active or documents are not verified
                            (String(customerKycStatus || '').toLowerCase() !== 'active' || !allDocumentsStatus)
                              ?
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="red"
                                strokeWidth="2"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <circle cx="12" cy="16" r="1" fill="red" />
                              </svg>
                              :
                              // Show warning icon if delivery is done and LRS is not signed
                              (areAllNonCancelledProductsDelivered && orderDetails?.overview?.lrs_signed !== true)
                                ?
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="red"
                                  strokeWidth="2"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="8" x2="12" y2="12" />
                                  <circle cx="12" cy="16" r="1" fill="red" />
                                </svg>
                                :
                                // Show checkmark if KYC is active, documents verified, and (delivery not done OR LRS signed)
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                </svg>
                          }
                        </span>
                      </div>
                    </TabsTrigger>
                  );
                }
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.value}
                    disabled={!tab.enabled}
                    className={`px-6 py-3.5 border-transparent border-t h-11 w-[215px] text-sm ${tab.value === activeTab &&
                      " rounded-none  border-blue-700 text-blue-700 shadow bg-[#EFF4FF]"
                      }`}
                  >
                    {
                      tab.value === "payments" ? (
                        <div className="flex gap-2 items-center">
                          {tab.label}{" "}
                          <span className=" text-center flex items-center gap-1.5">
                            {
                              isOrderFullyPaid ?
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                </svg> :
                                <svg
                                  width="16"
                                  height="17"
                                  viewBox="0 0 16 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8 0.5C3.592 0.5 0 4.092 0 8.5C0 12.908 3.592 16.5 8 16.5C12.408 16.5 16 12.908 16 8.5C16 4.092 12.408 0.5 8 0.5ZM11.824 6.66L7.288 11.196C7.176 11.308 7.024 11.372 6.864 11.372C6.704 11.372 6.552 11.308 6.44 11.196L4.176 8.932C3.944 8.7 3.944 8.316 4.176 8.084C4.408 7.852 4.792 7.852 5.024 8.084L6.864 9.924L10.976 5.812C11.208 5.58 11.592 5.58 11.824 5.812C12.056 6.044 12.056 6.42 11.824 6.66Z"
                                    fill="#D2D2D2"
                                  />
                                </svg>
                            }
                          </span>
                        </div>
                      ) :
                        tab.value === "delivery" ? (
                          <div className="flex gap-2 items-center">
                            {tab.label}{" "}
                            <span className=" text-center flex items-center gap-1.5">
                              {
                                areAllNonCancelledProductsDelivered ?
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 0C3.592 0 0 3.592 0 8C0 12.408 3.592 16 8 16C12.408 16 16 12.408 16 8C16 3.592 12.408 0 8 0ZM11.824 6.16L7.288 10.696C7.176 10.808 7.024 10.872 6.864 10.872C6.704 10.872 6.552 10.808 6.44 10.696L4.176 8.432C3.944 8.2 3.944 7.816 4.176 7.584C4.408 7.352 4.792 7.352 5.024 7.584L6.864 9.424L10.976 5.312C11.208 5.08 11.592 5.08 11.824 5.312C12.056 5.544 12.056 5.92 11.824 6.16Z" fill="#3FB73D" />
                                  </svg> :
                                  <svg
                                    width="16"
                                    height="17"
                                    viewBox="0 0 16 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M8 0.5C3.592 0.5 0 4.092 0 8.5C0 12.908 3.592 16.5 8 16.5C12.408 16.5 16 12.908 16 8.5C16 4.092 12.408 0.5 8 0.5ZM11.824 6.66L7.288 11.196C7.176 11.308 7.024 11.372 6.864 11.372C6.704 11.372 6.552 11.308 6.44 11.196L4.176 8.932C3.944 8.7 3.944 8.316 4.176 8.084C4.408 7.852 4.792 7.852 5.024 8.084L6.864 9.924L10.976 5.812C11.208 5.58 11.592 5.58 11.824 5.812C12.056 6.044 12.056 6.42 11.824 6.66Z"
                                      fill="#D2D2D2"
                                    />
                                  </svg>
                              }
                            </span>
                          </div>
                        ) :
                          <div className="flex gap-2 items-center">{tab.label}</div>
                    }
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <div className="flex  w-full justify-between gap-10">
              <div className="flex-1">
                <TabsContent
                  data-state={activeTab === "overview" ? "active" : ""}
                  className=" gap-10 flex flex-col rounded-xl w-full overflow-auto"
                  value="overview"
                >
                  <div className="p-3">
                    <Overview
                      tripData={orderDetails?.trip_details!!}
                      param={param}
                      orderdetails={orderDetails?.overview!!}
                      cardList={orderDetails?.card_details || []}
                      setIsEditing={setIsEditing}
                      setTempNote={setTempNote}
                      setNote={setNote}
                      isEditing={isEditing}
                      openDetailNote={openDetailNote}
                      note={note}
                      milestones={milestone}
                      tempNote={tempNote}
                      buttonState={isDisabledAll.disabled}
                      orderType={orderType}

                      onCustomerClick={(customerId: number) => {
                        setTab("travellers");
                        setSelectedCustomerId(customerId);

                      }}
                      setTab={(val) => {
                        setTab(val);
                      }}
                      milestones={milestone}
                    />
                  </div>
                </TabsContent>
                <TabsContent
                  className="gap-10 flex flex-col rounded-xl w-full"
                  value="product-charges"
                >
                  <div className="p-3">
                    {/* <AmountCharges
                    orderdetails={orderDetails?.amount_and_charges!!}
                    disCountinfo={orderDetails?.discount_info!! || {}}
                    liveStage={isDisabledAll?.disabled}
                    milestone={milestone}
                    payable={orderDetails?.overview?.amount_and_charges?.total_payable ?? 0}
                    paid={orderDetails?.overview?.amount_and_charges?.total_paid ?? 0}
                  /> */}
                    <ProductAndCharges
                      deliveryData={productDeliveryArray}
                      orderType={orderType || ''}
                      isInvoiceAvalaible={Boolean(isInvoiceAvalaible)}
                      orderdetails={orderDetails!}
                      disCountinfo={orderDetails?.discount_info!! || {}}
                      payable={
                        orderDetails?.overview?.amount_and_charges
                          ?.total_payable ?? 0
                      }
                      paid={
                        orderDetails?.overview?.amount_and_charges?.total_paid ??
                        0
                      }
                      milestones={milestone}
                    />
                  </div>
                </TabsContent>
                <TabsContent
                  className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                  value="travellers"
                >
                  <div className="p-3">
                    <Customers
                      isDeliveryFulfilled={areAllNonCancelledProductsDelivered}
                      isLrsSigned={orderDetails?.overview?.lrs_signed || false}
                      order_documents={orderDetails?.order_documents || []}
                      customers={orderDetails?.customers!!}
                      selectedCustomerId={selectedCustomerId}
                      onCustomerTabClick={(customerId: number) =>
                        setSelectedCustomerId(customerId)
                      }
                      documentsButtonState={{
                        disabled: !disabled || isDisabledAll?.disabled,
                        visible: disabled,
                      }}
                      paymentButtonState={paymentButtonState}
                    />
                  </div>
                </TabsContent>
                <TabsContent
                  className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                  value="delivery"
                >
                  <div className="px-3">
                    <Delivery
                      editButtonState={!hasActiveProductsForDelivery || areAllNonCancelledProductsDelivered || isDisabledAll.disabled}
                      products={orderDetails?.customers?.product_customer!!}
                      customers={orderDetails?.customers!!}
                      deliveryButtonState={deliveryButtonState}
                      deliveriesData={orderDetails?.delivery!!}
                      milestone={milestone}
                      cardDetails={orderDetails?.card_details}
                      branch={orderDetails?.branch}
                    />
                  </div>
                </TabsContent>
                <TabsContent
                  className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                  value="payments"
                >
                  <div className="px-3">
                    <Payments
                      order_type={orderType as 'sell' | 'buy'}
                      bank_details={orderDetails?.bank_details}
                      payable={
                        orderDetails?.overview?.amount_and_charges
                          ?.total_payable ?? 0
                      }
                      paid={
                        orderDetails?.overview?.amount_and_charges?.total_paid ??
                        0
                      }
                      balance={
                        orderDetails?.overview?.amount_and_charges?.balance ?? 0
                      }
                      payments={orderDetails?.payment_detail ?? []}
                      paymentButtonState={paymentButtonState}
                      orderDetails={{
                        customers_id: customer_id,
                      }}
                      milestones={milestone}
                    />
                  </div>
                </TabsContent>
                <TabsContent
                  className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                  value="other-details"
                >
                  <div className="px-3">
                    <OtherDetails orderdetails={orderDetails?.other_detail!!} />
                  </div>
                </TabsContent>
              </div>

            </div>

          </Tabs>

          <div className="flex justify-end mt-4">
            {(!isOrderCancelled && !isDisabledAll?.disabled) && (<button
              onClick={() => navigate(`/cancel/forex/${id}`)}
              className="px-4 py-2 mb-2 bg-[#F9F9F9] border border-[#D2D2D2] rounded-lg disabled:opacity-50"
            >
              Cancel Order
            </button>)}
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
      {isConfirmBooking && (
        <ConfirmBookingModal
          onClose={() => setIsConfirmBooking(false)}
          onSubmit={handleConfirmSubmit}
          initialData={oldSchemaData || []}
        />
      )}
      <div className="flex flex-col w-[300px] h-full">
        <MilestoneTracker milestones={milestone} />
      </div>

    </div>
  );
};

export default GenericOrderDetailsPage;
